/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * CampaignInfo Table component
 *
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.32.0
 * @since 1.12.0
 */

import { useCallback, useEffect } from 'react';

import { useSelector } from 'react-redux';

import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';

import { PageTitle, Table } from '../../../../components/common';
import { renderDropFilterButton } from '../../../../components/common/PageTitle/PageTitle';
import { useTable } from '../../../../components/Table/hooks/useTable';
import { ITableField } from '../../../../components/Table/reducers/tableReducer';
import TableColumnUtil from '../../../../components/Table/TableColumnUtil';
import { ColumnFieldType } from '../../../../components/Table/types/columnTypes';
import {
    MapTableNameToApiPath,
    MapTableNameToBasketApiPath,
    MapTableNameToListApiPath,
} from '../../../../components/Table/types/tableMapTypes';
import { ISwitchCheckboxPayload, TableNameType } from '../../../../components/Table/types/tableTypes';
import { ICommonTranslation, ICorporateTranslation } from '../../../../modules/internationalization/types';
import { IPageCampaignInfoTranslation } from '../../../../modules/internationalization/types/pages/campaignInfo';
import { IRouteParams } from '../../../../routes/constants/routesConstants';
import { stringFormatParams } from '../../../../utils/stringFormat';
import { ICampaignInfoGeneral } from '../../types/campaignInfoTypes';
import {
    ECampaignInfoTableSubscriberField,
    ICampaignInfoTableColumnUtilProps,
    mapCampaignImprovedInfoTypeToTableBasketApiPath,
    mapCampaignInfoTypeToTableApiPath,
    mapCampaignInfoTypeToTableBasketApiPath,
    mapCampaignInfoTypeToTableListApiPath,
    mapCampaignInfoTypeToTableName,
    mapTableNameToColumnUtils,
    TTableTranslation,
} from './CampaignInfoTableCells';

export interface ICampaignInfoTable
    extends Pick<IRouteParams, 'campaignId'>, Pick<ICampaignInfoGeneral, 'type'> {
    translationCampaignInfo: IPageCampaignInfoTranslation;
    translationCommon: ICommonTranslation & ICorporateTranslation;
}

const CampaignInfoTable = (props: ICampaignInfoTable) => {

    const {
        type,
        campaignId,
        translationCampaignInfo,
        translationCommon,
    } = props;

    const getTableName = useCallback(() => mapCampaignInfoTypeToTableName[type], [type]);
    const isCampaignsImprovementsFeature = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CAMPAIGNS_IMPROVEMENT));

    // get table props
    const {
        basketAllIn,
        componentsFeature,
        count,
        data,
        fields,
        isLoading,
        isTableResizeEnabled,
        sort,

        dropFilterAndSort,
        isFilterSelected,
        loadTableList,
        loadTableMeta,
        saveResizedChange,
        setTableSort,
        switchCheckbox,
    } = useTable(getTableName());

    useEffect(() => {
        if (campaignId) {
            const tableName = getTableName();
            // meta
            MapTableNameToApiPath[tableName] =
                stringFormatParams(mapCampaignInfoTypeToTableApiPath[type], { campaignId });
            // list
            MapTableNameToListApiPath[tableName] =
                stringFormatParams(mapCampaignInfoTypeToTableListApiPath[type], { campaignId });
            // basket
            MapTableNameToBasketApiPath[tableName] = isCampaignsImprovementsFeature
                ? stringFormatParams(mapCampaignImprovedInfoTypeToTableBasketApiPath[type], { campaignId })
                : stringFormatParams(mapCampaignInfoTypeToTableBasketApiPath[type], { campaignId });

            loadTableMeta();
        }
    }, [campaignId, getTableName, isCampaignsImprovementsFeature, loadTableMeta, type]);

    const clearFilterHandler = useCallback(() => dropFilterAndSort({}), [dropFilterAndSort]);

    const onSwitchCheckbox = useCallback((payload: ISwitchCheckboxPayload) => {
        switchCheckbox({ ...payload, params: { campaignId } });
    }, [campaignId, switchCheckbox]);

    const getTableColumnUtil = useCallback(() => {
        const tableName = getTableName();
        return getCampaignInfoTableColumnUtil<ECampaignInfoTableSubscriberField>(
            {
                componentsFeature,
                fields: fields as Array<ITableField<ECampaignInfoTableSubscriberField>>,
                isTableResizeEnabled,
                translation: { ...translationCommon, table: translationCampaignInfo[type].table },
                isDisableSort: isCampaignsImprovementsFeature,
            },
            tableName,
        );
    }, [
        componentsFeature,
        fields,
        getTableName,
        isCampaignsImprovementsFeature,
        isTableResizeEnabled,
        translationCampaignInfo,
        translationCommon,
        type,
    ]);

    return (
        <div>
            <PageTitle
                count={count}
                title={translationCampaignInfo[type].title.label}
            >
                {
                    isFilterSelected() && renderDropFilterButton({
                        onClick: clearFilterHandler,
                        value: translationCampaignInfo[type].title.dropFilters,
                    })
                }
            </PageTitle>
            <Table
                basketAllIn={basketAllIn}
                columns={getTableColumnUtil().customizeTableColumns()}
                counter={count}
                data={data}
                fields={fields}
                isLoading={isLoading}
                loadTableList={loadTableList}
                saveResizedChange={saveResizedChange}
                setTableSort={setTableSort}
                showCheckedColumn={true}
                sort={sort}
                switchCheckbox={onSwitchCheckbox}
                tableDataName={getTableName()}
                tableName={getTableName()}
            />
        </div>
    );
};

export function getCampaignInfoTableColumnUtil<T extends ColumnFieldType>(
    props: ICampaignInfoTableColumnUtilProps<T>,
    tableName: TableNameType,
): TableColumnUtil<T, TTableTranslation> {
    return new TableColumnUtil({
        ...mapTableNameToColumnUtils[tableName],
        ...props,
        getColumnFixedType: () => false,
    });
}

export default CampaignInfoTable;
